// SiteUp: os mundos do Hub. Fonte única do mapa de navegação.
// `sidebarName` é o `name` antigo do item da sidebar (menuSections / Sidebar.vue)
// e só entra na migração de preferência. Filho de submenu não tem sidebarName.
//
// Gates de destino:
//   featureFlag    flag da conta (usePolicy)
//   stacklabOnly   só na instalação Stacklab
//   desktopOnly    só na superfície de onboarding (desktop)
//   adminOnly      só para currentUser.role === 'administrator'
//   enabledGetter  getter Vuex que o toggle da conta liga/desliga

import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { AI_CENTER_MODULES } from '../aiCenter/aiCenterModules';
import { AI_CENTER_AREAS } from '../aiCenter/aiCenterAreas';

export const HIDDEN_WORLDS_KEY = 'sidebar_hidden_worlds';
export const LEGACY_HIDDEN_ITEMS_KEY = 'sidebar_hidden_menu_items';

const GROUP_LABEL_KEYS = Object.freeze({
  day_to_day: 'HUB.GROUPS.DAY_TO_DAY',
  tasks: 'HUB.GROUPS.TASKS',
  records: 'HUB.GROUPS.RECORDS',
  configuration: 'HUB.GROUPS.CONFIGURATION',
  campaigns: 'HUB.GROUPS.CAMPAIGNS',
  knowledge: 'HUB.GROUPS.KNOWLEDGE',
  behavior: 'HUB.GROUPS.BEHAVIOR',
  operation: 'HUB.GROUPS.OPERATION',
  testing: 'HUB.GROUPS.TESTING',
  tracking: 'HUB.GROUPS.TRACKING',
  channels: 'HUB.GROUPS.CHANNELS',
  personalization: 'HUB.GROUPS.PERSONALIZATION',
  account: 'HUB.GROUPS.ACCOUNT',
  // Ações de Marketing: o que a equipe dispara (agora ou com data marcada).
  dispatches: 'HUB.GROUPS.DISPATCHES',
  scheduled: 'HUB.GROUPS.SCHEDULED',
  // Automações: o que roda sozinho, do Flow Builder ao histórico.
  flows: 'HUB.GROUPS.FLOWS',
  sequences: 'HUB.GROUPS.SEQUENCES',
  captain: 'HUB.GROUPS.CAPTAIN',
  rules: 'HUB.GROUPS.RULES',
  history: 'HUB.GROUPS.HISTORY',
  // Números de cada área: os relatórios moram dentro do mundo que medem.
  numbers: 'HUB.GROUPS.NUMBERS',
  other: 'HUB.GROUPS.OTHER',
});

// Relatórios agora moram numa casa só (`/reports`), com abas por área. Cada
// mundo ganha UMA entrada "Relatórios", que abre a casa já na aba daquela
// área; o mundo Medir continua no mapa (busca, "Voltar para") com o índice
// completo, e todo item dele aponta para uma rota da casa, sem link órfão.
const REPORT_DESTINATIONS = Object.freeze({
  service: {
    id: 'reports_service',
    groupKey: 'numbers',
    labelKey: 'HUB.DESTINATIONS.REPORTS_HOME_SERVICE',
    subtitleKey: 'HUB.SUBTITLES.REPORTS_HOME_SERVICE',
    icon: 'i-ph-chart-bar',
    routeName: 'account_overview_reports',
  },
  sales: {
    id: 'reports_sales',
    groupKey: 'numbers',
    labelKey: 'HUB.DESTINATIONS.REPORTS_HOME_SALES',
    subtitleKey: 'HUB.SUBTITLES.REPORTS_HOME_SALES',
    icon: 'i-ph-chart-bar',
    routeName: 'kanban_reports',
    featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
  },
  campaigns: {
    id: 'reports_campaigns',
    groupKey: 'numbers',
    labelKey: 'HUB.DESTINATIONS.REPORTS_HOME_CAMPAIGNS',
    subtitleKey: 'HUB.SUBTITLES.REPORTS_HOME_CAMPAIGNS',
    icon: 'i-ph-chart-bar',
    routeName: 'campaign_reports',
    adminOnly: true,
  },
  ai: {
    id: 'reports_ai',
    groupKey: 'numbers',
    labelKey: 'HUB.DESTINATIONS.REPORTS_HOME_AI',
    subtitleKey: 'HUB.SUBTITLES.REPORTS_HOME_AI',
    icon: 'i-ph-chart-bar',
    routeName: 'ai_reports',
    adminOnly: true,
    featureFlag: FEATURE_FLAGS.CAPTAIN_V2,
  },
  calls: {
    id: 'reports_calls',
    groupKey: 'numbers',
    labelKey: 'HUB.DESTINATIONS.REPORTS_HOME_CALLS',
    subtitleKey: 'HUB.SUBTITLES.REPORTS_HOME_CALLS',
    icon: 'i-ph-chart-bar',
    routeName: 'voip_reports',
    featureFlag: FEATURE_FLAGS.VOIP_CALLS,
  },
});

// Os quatro cards do Hub. Medir e Configurar ficam no mapa (busca, "Voltar
// para", preferências) sem card: relatórios entram nas áreas e as
// configurações têm acesso próprio no rodapé do Hub.
export const hubCardWorlds = worlds =>
  (worlds || []).filter(world => world.hubCard !== false);

export const groupDestinations = destinations => {
  const groups = [];
  const byId = new Map();
  (destinations || []).forEach(destination => {
    const id = destination.groupKey || 'other';
    let group = byId.get(id);
    if (!group) {
      group = {
        id,
        labelKey: GROUP_LABEL_KEYS[id] || GROUP_LABEL_KEYS.other,
        destinations: [],
      };
      byId.set(id, group);
      groups.push(group);
    }
    group.destinations.push(destination);
  });
  return groups.filter(group => group.destinations.length > 0);
};

// Uma entrada por área da Central, na ordem do trilho (Falam com o cliente,
// Trabalham por dentro, Qualidade), com endereço próprio da área
// (`/v2/ai-center/comentarios`). O id continua `ai_center_<id interno>`:
// preferências, busca e módulos da conta já usam esses ids. Reports da IA é
// só de administrador, como na Central.
// A área que junta filas (Oportunidades, D42) não é módulo: o Hub segue com
// uma entrada por fila.
const AI_CENTER_DESTINATIONS = AI_CENTER_AREAS.filter(
  area => !area.members
).map(area => {
  const module = AI_CENTER_MODULES.find(item => item.id === area.id);
  return {
    id: `ai_center_${area.id}`,
    groupKey: 'operation',
    labelKey: `HUB.DESTINATIONS.AI_CENTER_${area.id.toUpperCase()}`,
    subtitleKey: 'HUB.SUBTITLES.AI_CENTER',
    icon: area.icon,
    routeName: 'ai_center_area',
    params: { area: area.slug },
    featureFlag: module?.flag,
    aiCenterModule: area.id,
    ...(area.adminOnly ? { adminOnly: true } : {}),
  };
});

export const HUB_WORLDS = [
  {
    id: 'attendance',
    labelKey: 'HUB.WORLDS.ATTENDANCE',
    descriptionKey: 'HUB.WORLDS.ATTENDANCE_DESC',
    icon: 'i-ph-headset',
    featured: true,
    primaryAction: {
      destinationId: 'conversations',
      labelKey: 'HUB.OPEN_QUEUE',
      bannerKey: 'HUB.ATTENDANCE_BANNER',
    },
    destinations: [
      {
        id: 'inbox',
        groupKey: 'day_to_day',
        sidebarName: 'Inbox',
        labelKey: 'HUB.DESTINATIONS.INBOX',
        subtitleKey: 'HUB.SUBTITLES.INBOX',
        icon: 'i-ph-tray',
        routeName: 'inbox_view',
      },
      {
        id: 'conversations',
        groupKey: 'day_to_day',
        sidebarName: 'Conversation',
        labelKey: 'SIDEBAR.CONVERSATIONS',
        subtitleKey: 'HUB.SUBTITLES.CONVERSATIONS',
        icon: 'i-ph-chat-circle',
        routeName: 'home',
      },
      {
        id: 'internal_chat',
        groupKey: 'day_to_day',
        sidebarName: 'InternalChat',
        labelKey: 'SIDEBAR.INTERNAL_CHAT',
        subtitleKey: 'HUB.SUBTITLES.INTERNAL_CHAT',
        icon: 'i-ph-users',
        routeName: 'internal_chat',
      },
      {
        id: 'voip',
        groupKey: 'day_to_day',
        sidebarName: 'Voip Calls',
        labelKey: 'SIDEBAR.VOIP_CALLS',
        subtitleKey: 'HUB.SUBTITLES.VOIP',
        icon: 'i-ph-phone-call',
        routeName: 'voip_calls',
        featureFlag: FEATURE_FLAGS.VOIP_CALLS,
      },
      // Caixas e canais: a configuração que a pessoa de atendimento mais
      // abre (conectar WhatsApp, Instagram, site). Só administrador.
      {
        id: 'inboxes',
        groupKey: 'configuration',
        labelKey: 'HUB.DESTINATIONS.INBOXES',
        subtitleKey: 'HUB.SUBTITLES.INBOXES',
        icon: 'i-ph-tray',
        routeName: 'settings_inbox_list',
        adminOnly: true,
      },
      REPORT_DESTINATIONS.service,
      REPORT_DESTINATIONS.calls,
    ],
  },
  {
    id: 'sales',
    labelKey: 'HUB.WORLDS.SALES',
    descriptionKey: 'HUB.WORLDS.SALES_DESC',
    icon: 'i-ph-chart-bar',
    primaryAction: {
      destinationId: 'kanban',
      labelKey: 'HUB.OPEN_KANBAN',
      bannerKey: 'HUB.SALES_BANNER',
    },
    destinations: [
      {
        id: 'kanban',
        groupKey: 'day_to_day',
        sidebarName: 'Kanban',
        labelKey: 'HUB.DESTINATIONS.KANBAN',
        subtitleKey: 'HUB.SUBTITLES.KANBAN',
        icon: 'i-ph-squares-four',
        routeName: 'kanban_board',
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      {
        id: 'agenda',
        groupKey: 'day_to_day',
        labelKey: 'HUB.DESTINATIONS.AGENDA',
        subtitleKey: 'HUB.SUBTITLES.AGENDA',
        icon: 'i-ph-calendar',
        routeName: 'kanban_board',
        query: { view: 'agenda' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      // As três sub-views abaixo respondem uma pergunta que o quadro não
      // responde (onde os cards estão, como o funil está indo, como ele é
      // configurado) e até aqui só existiam atrás do seletor de visão ou do
      // menu "..." do KanbanHeader.
      // Ficaram de fora de propósito: 'list' mostra o mesmo dado do quadro em
      // outro layout (o seletor de visão resolve), 'offer_packages' é filho de
      // Ofertas e só faz sentido lá dentro, e 'stage_automations' depende de
      // ?stage=, sem o qual a tela abre sem etapa e o salvar aborta.
      {
        id: 'kanban_map',
        groupKey: 'day_to_day',
        labelKey: 'HUB.DESTINATIONS.KANBAN_MAP',
        subtitleKey: 'HUB.SUBTITLES.KANBAN_MAP',
        icon: 'i-ph-map-pin',
        routeName: 'kanban_board',
        query: { view: 'map' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
        enabledGetter: 'kanban/getMapViewEnabled',
      },
      {
        id: 'kanban_dashboard',
        groupKey: 'day_to_day',
        labelKey: 'HUB.DESTINATIONS.KANBAN_DASHBOARD',
        subtitleKey: 'HUB.SUBTITLES.KANBAN_DASHBOARD',
        icon: 'i-ph-chart-pie',
        routeName: 'kanban_board',
        query: { view: 'dashboard' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
        enabledGetter: 'kanban/getDashboardViewEnabled',
        adminOnly: true,
      },
      {
        id: 'kanban_funnels',
        groupKey: 'configuration',
        labelKey: 'HUB.DESTINATIONS.FUNNELS',
        subtitleKey: 'HUB.SUBTITLES.FUNNELS',
        icon: 'i-ph-funnel',
        routeName: 'kanban_board',
        query: { view: 'funnels' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
        adminOnly: true,
      },
      {
        id: 'offers',
        groupKey: 'configuration',
        labelKey: 'HUB.DESTINATIONS.OFFERS',
        subtitleKey: 'HUB.SUBTITLES.OFFERS',
        icon: 'i-ph-package',
        routeName: 'kanban_board',
        query: { view: 'offers' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
        stacklabOnly: true,
      },
      {
        id: 'templates',
        groupKey: 'configuration',
        labelKey: 'HUB.DESTINATIONS.TEMPLATES',
        subtitleKey: 'HUB.SUBTITLES.TEMPLATES',
        icon: 'i-ph-chat-teardrop-text',
        routeName: 'kanban_board',
        query: { view: 'templates' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
        stacklabOnly: true,
      },
      {
        id: 'tasks',
        groupKey: 'tasks',
        sidebarName: 'Tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS',
        subtitleKey: 'HUB.SUBTITLES.TASKS',
        icon: 'i-ph-check',
        routeName: 'tasks',
      },
      {
        id: 'tasks_today',
        groupKey: 'tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS_TODAY',
        subtitleKey: 'HUB.SUBTITLES.TASKS_TODAY',
        icon: 'i-ph-calendar',
        routeName: 'tasks',
        query: { preset: 'today' },
      },
      {
        id: 'tasks_overdue',
        groupKey: 'tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS_OVERDUE',
        subtitleKey: 'HUB.SUBTITLES.TASKS_OVERDUE',
        icon: 'i-ph-alarm',
        routeName: 'tasks',
        query: { preset: 'overdue' },
      },
      {
        id: 'tasks_mine',
        groupKey: 'tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS_MINE',
        subtitleKey: 'HUB.SUBTITLES.TASKS_MINE',
        icon: 'i-ph-user',
        routeName: 'tasks',
        query: { preset: 'mine' },
      },
      {
        id: 'tasks_done',
        groupKey: 'tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS_DONE',
        subtitleKey: 'HUB.SUBTITLES.TASKS_DONE',
        icon: 'i-ph-check-circle',
        routeName: 'tasks',
        query: { preset: 'done' },
      },
      {
        id: 'tasks_blocked',
        groupKey: 'tasks',
        labelKey: 'HUB.DESTINATIONS.TASKS_BLOCKED',
        subtitleKey: 'HUB.SUBTITLES.TASKS_BLOCKED',
        icon: 'i-ph-lock',
        routeName: 'tasks',
        query: { preset: 'blocked' },
      },
      {
        id: 'contacts',
        groupKey: 'records',
        sidebarName: 'Contacts',
        labelKey: 'SIDEBAR.CONTACTS',
        subtitleKey: 'HUB.SUBTITLES.CONTACTS',
        icon: 'i-ph-address-book',
        routeName: 'contacts_dashboard_index',
        query: { page: 1 },
      },
      {
        id: 'companies',
        groupKey: 'records',
        sidebarName: 'Companies',
        labelKey: 'SIDEBAR.COMPANIES',
        subtitleKey: 'HUB.SUBTITLES.COMPANIES',
        icon: 'i-ph-buildings',
        routeName: 'companies_dashboard_index',
        query: { page: 1 },
      },
      REPORT_DESTINATIONS.sales,
    ],
  },
  {
    id: 'marketing',
    labelKey: 'HUB.WORLDS.MARKETING',
    descriptionKey: 'HUB.WORLDS.MARKETING_DESC',
    icon: 'i-ph-megaphone',
    primaryAction: {
      destinationId: 'campaigns_livechat',
      labelKey: 'HUB.OPEN_CAMPAIGNS',
      bannerKey: 'HUB.MARKETING_BANNER',
    },
    destinations: [
      {
        id: 'campaigns_livechat',
        groupKey: 'dispatches',
        sidebarName: 'Campaigns',
        labelKey: 'SIDEBAR.LIVE_CHAT',
        subtitleKey: 'HUB.SUBTITLES.CAMPAIGNS_LIVECHAT',
        icon: 'i-ph-chat-circle-dots',
        routeName: 'campaigns_livechat_index',
      },
      {
        id: 'campaigns_sms',
        groupKey: 'dispatches',
        labelKey: 'SIDEBAR.SMS',
        subtitleKey: 'HUB.SUBTITLES.CAMPAIGNS_SMS',
        icon: 'i-ph-chat-text',
        routeName: 'campaigns_sms_index',
      },
      {
        id: 'campaigns_whatsapp',
        groupKey: 'dispatches',
        labelKey: 'SIDEBAR.WHATSAPP',
        subtitleKey: 'HUB.SUBTITLES.CAMPAIGNS_WHATSAPP',
        icon: 'i-ri-whatsapp-line',
        routeName: 'campaigns_whatsapp_index',
      },
      {
        id: 'campaigns_scheduled',
        groupKey: 'scheduled',
        labelKey: 'SIDEBAR.SCHEDULED',
        subtitleKey: 'HUB.SUBTITLES.CAMPAIGNS_SCHEDULED',
        icon: 'i-ph-calendar-check',
        routeName: 'campaigns_scheduled_index',
      },
      {
        id: 'whatsapp_groups',
        groupKey: 'dispatches',
        sidebarName: 'WhatsApp Groups',
        labelKey: 'HUB.DESTINATIONS.WHATSAPP_GROUPS',
        subtitleKey: 'HUB.SUBTITLES.WHATSAPP_GROUPS',
        icon: 'i-ph-users',
        routeName: 'whatsapp_groups_campaigns',
        featureFlag: FEATURE_FLAGS.WHATSAPP_GROUPS,
      },
      // E-mail como ação de marketing: hoje o envio por e-mail é a sequência.
      {
        id: 'email_actions',
        groupKey: 'dispatches',
        labelKey: 'HUB.DESTINATIONS.EMAIL',
        subtitleKey: 'HUB.SUBTITLES.EMAIL',
        icon: 'i-ph-envelope',
        routeName: 'email_sequences',
        featureFlag: FEATURE_FLAGS.EMAIL_SEQUENCES,
      },
      {
        id: 'scheduled_messages',
        groupKey: 'scheduled',
        sidebarName: 'Scheduled Messages',
        labelKey: 'SCHEDULE_MESSAGES.LIST.TITLE',
        subtitleKey: 'HUB.SUBTITLES.SCHEDULED_MESSAGES',
        icon: 'i-ph-clock',
        routeName: 'kanban_board',
        query: { view: 'scheduled_messages' },
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      REPORT_DESTINATIONS.campaigns,
    ],
  },
  {
    id: 'automation',
    labelKey: 'HUB.WORLDS.AUTOMATION',
    descriptionKey: 'HUB.WORLDS.AUTOMATION_DESC',
    icon: 'i-ph-sparkle',
    primaryAction: {
      destinationId: 'flow_automations',
      labelKey: 'HUB.OPEN_FLOWS',
      bannerKey: 'HUB.AUTOMATION_BANNER',
    },
    destinations: [
      // Fluxos primeiro: o Flow Builder é a porta de entrada das automações
      // (comentário do Instagram, mensagem, botões, etiquetas, Kanban e IA).
      {
        id: 'flow_automations',
        groupKey: 'flows',
        sidebarName: 'Flow automations',
        labelKey: 'SIDEBAR.FLOW_AUTOMATIONS',
        subtitleKey: 'HUB.SUBTITLES.FLOW_AUTOMATIONS',
        icon: 'i-ph-git-branch',
        routeName: 'conversation_workflows_index',
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      // Modelo antigo de comentário para DM, mantido enquanto as contas migram
      // para o gatilho "Comentário no Instagram" do Flow Builder.
      {
        id: 'comment_ig',
        groupKey: 'flows',
        sidebarName: 'Comment to DM',
        labelKey: 'HUB.DESTINATIONS.COMMENT_IG',
        subtitleKey: 'HUB.SUBTITLES.COMMENT_IG',
        icon: 'i-ri-instagram-line',
        routeName: 'instagram_automation_index',
      },
      {
        id: 'comment_fb',
        groupKey: 'flows',
        sidebarName: 'FB Comment to DM',
        labelKey: 'HUB.DESTINATIONS.COMMENT_FB',
        subtitleKey: 'HUB.SUBTITLES.COMMENT_FB',
        icon: 'i-ri-facebook-line',
        routeName: 'facebook_automation_index',
      },
      {
        id: 'email_sequences',
        groupKey: 'sequences',
        sidebarName: 'Email Sequences',
        labelKey: 'HUB.DESTINATIONS.EMAIL_SEQUENCES',
        subtitleKey: 'HUB.SUBTITLES.EMAIL_SEQUENCES',
        icon: 'i-ph-envelope',
        routeName: 'email_sequences',
        featureFlag: FEATURE_FLAGS.EMAIL_SEQUENCES,
      },
      {
        id: 'captain_faqs',
        groupKey: 'captain',
        sidebarName: 'Captain',
        labelKey: 'SIDEBAR.CAPTAIN_RESPONSES',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_FAQS',
        icon: 'i-woot-captain',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_responses_index' },
      },
      {
        id: 'captain_documents',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_DOCUMENTS',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_DOCUMENTS',
        icon: 'i-ph-file-text',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_documents_index' },
      },
      {
        id: 'captain_scenarios',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_SCENARIOS',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_SCENARIOS',
        icon: 'i-ph-flow-arrow',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_scenarios_index' },
      },
      {
        id: 'captain_playground',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_PLAYGROUND',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_PLAYGROUND',
        icon: 'i-ph-flask',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_playground_index' },
      },
      {
        id: 'captain_inboxes',
        groupKey: 'captain',
        labelKey: 'HUB.DESTINATIONS.CAPTAIN_INBOXES',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_INBOXES',
        icon: 'i-ph-tray',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_inboxes_index' },
      },
      {
        id: 'captain_schedules',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_SCHEDULES',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_SCHEDULES',
        icon: 'i-ph-clock',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_schedules_index' },
      },
      {
        id: 'captain_tools',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_TOOLS',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_TOOLS',
        icon: 'i-ph-wrench',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_tools_index' },
      },
      {
        id: 'captain_settings',
        groupKey: 'captain',
        labelKey: 'SIDEBAR.CAPTAIN_SETTINGS',
        subtitleKey: 'HUB.SUBTITLES.CAPTAIN_SETTINGS',
        icon: 'i-ph-gear',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_settings_index' },
      },
      {
        id: 'captain_ai_engine',
        groupKey: 'captain',
        labelKey: 'HUB.DESTINATIONS.AI_ENGINE',
        subtitleKey: 'HUB.SUBTITLES.AI_ENGINE',
        icon: 'i-ph-cpu',
        routeName: 'ai_agents_provider',
      },
      {
        id: 'automation_rules',
        groupKey: 'rules',
        labelKey: 'HUB.DESTINATIONS.AUTOMATION_RULES',
        subtitleKey: 'HUB.SUBTITLES.AUTOMATION_RULES',
        icon: 'i-ph-list-checks',
        routeName: 'automation_list',
        adminOnly: true,
      },
      REPORT_DESTINATIONS.ai,
      {
        id: 'automation_logs',
        groupKey: 'history',
        sidebarName: 'Automation Logs',
        labelKey: 'AUTOMATION_EVENTS.SIDEBAR',
        subtitleKey: 'HUB.SUBTITLES.AUTOMATION_LOGS',
        icon: 'i-ph-list-checks',
        routeName: 'automation_logs',
      },
    ],
  },
  {
    id: 'ai_center',
    labelKey: 'HUB.WORLDS.AI_CENTER',
    descriptionKey: 'HUB.WORLDS.AI_CENTER_DESC',
    icon: 'i-ph-sparkle',
    primaryAction: {
      destinationId: 'ai_center_home',
      labelKey: 'HUB.OPEN_AI_CENTER',
      bannerKey: 'HUB.AI_CENTER_BANNER',
    },
    destinations: [
      {
        id: 'ai_center_home',
        groupKey: 'operation',
        labelKey: 'HUB.DESTINATIONS.AI_CENTER',
        subtitleKey: 'HUB.SUBTITLES.AI_CENTER',
        icon: 'i-ph-sparkle',
        routeName: 'ai_center',
        featureFlag: FEATURE_FLAGS.AI_ACTION_CENTER,
        aiCenterModule: 'any',
      },
      ...AI_CENTER_DESTINATIONS,
    ],
  },
  {
    id: 'analytics',
    hubCard: false,
    labelKey: 'HUB.WORLDS.ANALYTICS',
    descriptionKey: 'HUB.WORLDS.ANALYTICS_DESC',
    icon: 'i-ph-chart-bar',
    primaryAction: {
      destinationId: 'reports_overview',
      labelKey: 'HUB.OPEN_REPORTS',
      bannerKey: 'HUB.ANALYTICS_BANNER',
    },
    destinations: [
      {
        id: 'reports_overview',
        groupKey: 'day_to_day',
        sidebarName: 'Reports',
        labelKey: 'SIDEBAR.REPORTS_OVERVIEW',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_OVERVIEW',
        icon: 'i-ph-gauge',
        routeName: 'account_overview_reports',
      },
      {
        id: 'reports_conversation',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_CONVERSATION',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_CONVERSATION',
        icon: 'i-ph-chat-circle',
        routeName: 'conversation_reports',
      },
      {
        id: 'reports_csat',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.CSAT',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_CSAT',
        icon: 'i-ph-smiley',
        routeName: 'csat_reports',
      },
      {
        id: 'reports_sla',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_SLA',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_SLA',
        icon: 'i-ph-warning-circle',
        routeName: 'sla_reports',
      },
      {
        id: 'reports_bot',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_BOT',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_BOT',
        icon: 'i-ph-robot',
        routeName: 'bot_reports',
      },
      {
        id: 'reports_kanban',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_KANBAN',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_KANBAN',
        icon: 'i-ph-squares-four',
        routeName: 'kanban_reports',
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      {
        id: 'reports_agent',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_AGENT',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_AGENT',
        icon: 'i-ph-identification-card',
        routeName: 'agent_reports_index',
      },
      {
        id: 'reports_label',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_LABEL',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_LABEL',
        icon: 'i-ph-tag',
        routeName: 'label_reports_index',
      },
      {
        id: 'reports_inbox',
        groupKey: 'day_to_day',
        labelKey: 'HUB.DESTINATIONS.REPORTS_INBOX',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_INBOX',
        icon: 'i-ph-tray',
        routeName: 'inbox_reports_index',
      },
      {
        id: 'reports_team',
        groupKey: 'day_to_day',
        labelKey: 'SIDEBAR.REPORTS_TEAM',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_TEAM',
        icon: 'i-ph-users',
        routeName: 'team_reports_index',
      },
      {
        id: 'reports_conversions',
        groupKey: 'day_to_day',
        labelKey: 'HUB.DESTINATIONS.REPORTS_CONVERSIONS',
        subtitleKey: 'HUB.SUBTITLES.REPORTS_CONVERSIONS',
        icon: 'i-ph-share-fat',
        routeName: 'integration_events_report',
      },
      REPORT_DESTINATIONS.campaigns,
      REPORT_DESTINATIONS.ai,
      REPORT_DESTINATIONS.calls,
    ],
  },
  {
    id: 'system',
    hubCard: false,
    labelKey: 'HUB.WORLDS.SYSTEM',
    descriptionKey: 'HUB.WORLDS.SYSTEM_DESC',
    icon: 'i-ph-gear',
    primaryAction: {
      destinationId: 'settings',
      labelKey: 'HUB.OPEN_SETTINGS',
      bannerKey: 'HUB.SYSTEM_BANNER',
    },
    destinations: [
      {
        id: 'help_center',
        groupKey: 'account',
        sidebarName: 'Help Center',
        labelKey: 'SIDEBAR.HELP_CENTER.TITLE',
        subtitleKey: 'HUB.SUBTITLES.HELP_CENTER',
        icon: 'i-ph-question',
        routeName: 'help_center_index',
      },
      {
        id: 'help_guides',
        groupKey: 'account',
        labelKey: 'HUB.DESTINATIONS.HELP_GUIDES',
        subtitleKey: 'HUB.SUBTITLES.HELP_GUIDES',
        icon: 'i-ph-lifebuoy',
        routeName: 'help_guides',
        desktopOnly: true,
      },
      {
        id: 'release_notes',
        groupKey: 'account',
        labelKey: 'HUB.DESTINATIONS.RELEASE_NOTES',
        subtitleKey: 'HUB.SUBTITLES.RELEASE_NOTES',
        icon: 'i-ph-sparkle',
        // Sem desktopOnly: nota de versao se le no celular tambem.
        routeName: 'release_notes',
      },
      {
        id: 'settings',
        groupKey: 'account',
        sidebarName: 'Settings',
        labelKey: 'SIDEBAR.SETTINGS',
        subtitleKey: 'HUB.SUBTITLES.SETTINGS',
        icon: 'i-ph-gear',
        routeName: 'settings_menu',
      },
      {
        id: 'lead_scoring',
        groupKey: 'personalization',
        labelKey: 'HUB.DESTINATIONS.LEAD_SCORING',
        subtitleKey: 'HUB.SUBTITLES.LEAD_SCORING',
        icon: 'i-ph-lightning',
        routeName: 'lead_scoring_settings',
        featureFlag: FEATURE_FLAGS.LEAD_SCORING,
      },
      {
        id: 'integrations',
        groupKey: 'channels',
        sidebarName: 'Integrations',
        labelKey: 'HUB.DESTINATIONS.INTEGRATIONS',
        subtitleKey: 'HUB.SUBTITLES.INTEGRATIONS',
        icon: 'i-ph-plug',
        // Listagem Index.vue: o nome da rota é settings_applications.
        routeName: 'settings_applications',
        featureFlag: FEATURE_FLAGS.INTEGRATIONS,
      },
    ],
  },
];

// Tela de início de cada área, na versão "ação primeiro" escolhida em
// 12/09/2026: um destaque com o que a área faz e os números vivos, o resto
// em faixa. Cada bloco aponta para um destino do mapa acima pelo id; destino
// que a pessoa não pode ver (flag, papel, toggle) some do bloco sem quebrar
// a tela. O mapa completo continua servindo a busca e a barra "Voltar".
//
//   hero      qual destaque a área usa (os números vêm de useHubWorldStats)
//   actions   botões do destaque; `ghost` é o secundário; `query` entra na
//             rota do destino (a fila abre num chip, o Kanban abre o
//             formulário, os fluxos abrem o assistente)
//   chips     atalhos em linha; `inboxes: true` acrescenta um chip por caixa
//   channels  chips de canal do destaque de Ações de Marketing (o botão
//             "Criar" abre o canal escolhido)
//   columns   duas colunas de blocos com rótulo
//   list      lista viva (campanhas, fluxos) que useHubWorldStats preenche
//   rail      coluna de blocos ao lado da lista
//   strip     faixa de blocos menores
//   links     links discretos no rodapé (configuração, relatórios)
//   labelKey/subtitleKey num item trocam o texto do destino só nesta tela.
export const HUB_HOMES = Object.freeze({
  attendance: {
    hero: 'queue',
    actions: [
      {
        id: 'queue',
        labelKey: 'HUB.OPEN_QUEUE',
        destinationId: 'conversations',
      },
      {
        id: 'next',
        labelKey: 'HUB.HOME.VIEW_UNASSIGNED',
        destinationId: 'conversations',
        query: { filter: 'unassigned' },
        ghost: true,
      },
    ],
    chips: {
      labelKey: 'HUB.HOME.QUEUE_BY',
      items: [
        {
          id: 'mine',
          labelKey: 'HUB.HOME.CHIP_MINE',
          destinationId: 'conversations',
          query: { filter: 'mine' },
          count: 'mine',
        },
        {
          id: 'unassigned',
          labelKey: 'HUB.HOME.CHIP_UNASSIGNED',
          destinationId: 'conversations',
          query: { filter: 'unassigned' },
          count: 'unassigned',
        },
        {
          id: 'all',
          labelKey: 'HUB.HOME.CHIP_ALL',
          destinationId: 'conversations',
          query: { filter: 'all' },
        },
      ],
      inboxes: true,
    },
    strip: {
      labelKey: 'HUB.HOME.ALSO_HERE',
      items: [
        { destinationId: 'internal_chat' },
        { destinationId: 'voip' },
        { destinationId: 'reports_service', labelKey: 'HUB.HOME.REPORTS' },
        { destinationId: 'inboxes', labelKey: 'HUB.HOME.INBOXES' },
      ],
    },
  },
  sales: {
    hero: 'funnel',
    actions: [
      { id: 'kanban', labelKey: 'HUB.OPEN_KANBAN', destinationId: 'kanban' },
      {
        id: 'new_deal',
        labelKey: 'HUB.HOME.NEW_DEAL',
        destinationId: 'kanban',
        query: { new: '1' },
        ghost: true,
      },
    ],
    columns: [
      {
        id: 'day',
        labelKey: 'HUB.HOME.YOUR_DAY',
        items: [{ destinationId: 'tasks' }, { destinationId: 'agenda' }],
      },
      {
        id: 'people',
        labelKey: 'HUB.HOME.PEOPLE',
        items: [{ destinationId: 'contacts' }, { destinationId: 'companies' }],
      },
    ],
    links: [
      { destinationId: 'reports_sales', labelKey: 'HUB.HOME.SALES_REPORTS' },
      { destinationId: 'kanban_funnels', labelKey: 'HUB.HOME.SETUP_FUNNELS' },
      { destinationId: 'kanban_map' },
    ],
  },
  marketing: {
    hero: 'new_action',
    // "Criar" abre o formulário do canal escolhido (?new=1 nas listas).
    channels: [
      { destinationId: 'campaigns_whatsapp', query: { new: '1' } },
      { destinationId: 'email_actions', query: { new: '1' } },
      { destinationId: 'campaigns_sms', query: { new: '1' } },
      {
        destinationId: 'whatsapp_groups',
        labelKey: 'HUB.HOME.CHANNEL_GROUPS',
        query: { new: '1' },
      },
      {
        destinationId: 'campaigns_livechat',
        labelKey: 'HUB.HOME.CHANNEL_SITE',
        query: { new: '1' },
      },
    ],
    list: {
      id: 'campaigns',
      labelKey: 'HUB.HOME.RUNNING_NOW',
      emptyKey: 'HUB.HOME.NO_CAMPAIGNS',
    },
    rail: {
      labelKey: 'HUB.GROUPS.SCHEDULED',
      items: [
        { destinationId: 'campaigns_scheduled' },
        { destinationId: 'scheduled_messages' },
      ],
    },
  },
  automation: {
    hero: 'flows',
    actions: [
      {
        id: 'new_flow',
        labelKey: 'HUB.HOME.NEW_FLOW',
        destinationId: 'flow_automations',
        query: { new: '1' },
      },
      {
        id: 'wizard',
        labelKey: 'FLOW_AUTOMATIONS.WIZARD.OPEN',
        destinationId: 'flow_automations',
        query: { wizard: 'comment_to_dm' },
        ghost: true,
      },
    ],
    list: {
      id: 'flows',
      labelKey: 'HUB.GROUPS.FLOWS',
      emptyKey: 'HUB.HOME.NO_FLOWS',
    },
    rail: {
      labelKey: 'HUB.HOME.ALSO_HERE',
      items: [
        { destinationId: 'email_sequences' },
        {
          destinationId: 'captain_faqs',
          labelKey: 'HUB.HOME.CAPTAIN',
          subtitleKey: 'HUB.HOME.CAPTAIN_SUB',
        },
        { destinationId: 'automation_rules' },
        { destinationId: 'automation_logs' },
      ],
    },
  },
  ai_center: {
    hero: 'ai_center',
    actions: [
      {
        id: 'open_ai_center',
        labelKey: 'HUB.OPEN_AI_CENTER',
        destinationId: 'ai_center_home',
      },
    ],
    rail: {
      labelKey: 'HUB.HOME.ALSO_HERE',
      items: [
        ...AI_CENTER_DESTINATIONS.map(destination => ({
          destinationId: destination.id,
        })),
      ],
    },
  },
});

// Todo id citado na tela de início precisa existir no mapa da mesma área;
// o spec varre isto. Aqui só a leitura: lista de ids por área.
export const homeDestinationIds = home =>
  [
    ...(home?.actions || []),
    ...(home?.chips?.items || []),
    ...(home?.channels || []),
    ...(home?.columns || []).flatMap(column => column.items || []),
    ...(home?.rail?.items || []),
    ...(home?.strip?.items || []),
    ...(home?.links || []),
  ]
    .map(item => item.destinationId)
    .filter(Boolean);

// Quantas opções a tela de início mostra de verdade para a pessoa (só os
// ids do HUB_HOMES que sobraram em `destinations` depois dos gates). Área sem
// tela de início (Medir, Configurar) conta os destinos do mapa.
export const homeOptionCount = world => {
  const home = HUB_HOMES[world?.id];
  if (!home) return world?.destinationCount ?? 0;
  const allowed = new Set((world.destinations || []).map(d => d.id));
  return new Set(homeDestinationIds(home).filter(id => allowed.has(id))).size;
};

// O papel do Kanban não está no meta da rota, então usePolicy não consegue
// julgar 'adminOnly'. Quem decide é useAdmin, como no dock.
export const destinationAllowedForRole = (destination, isAdmin) =>
  !destination.adminOnly || isAdmin === true;

// Fail-open de propósito: getter ausente (store ainda não carregou o config do
// Kanban) libera o destino em vez de sumir com ele no meio do boot.
export const destinationAllowedByToggle = (destination, getters = {}) =>
  !destination.enabledGetter || getters[destination.enabledGetter] !== false;

export const legacySidebarMap = () =>
  HUB_WORLDS.flatMap(world =>
    world.destinations
      .filter(destination => destination.sidebarName)
      .map(destination => ({
        id: destination.id,
        sidebarName: destination.sidebarName,
        labelKey: destination.labelKey,
        worldId: world.id,
        worldLabelKey: world.labelKey,
      }))
  );

export const worldPreferenceNames = world => {
  const names = new Set();
  world.destinations.forEach(destination => {
    if (destination.sidebarName) names.add(destination.sidebarName);
  });
  return [...names];
};

export const migrateHiddenWorlds = uiSettings => {
  if (Array.isArray(uiSettings?.sidebar_hidden_worlds)) {
    return uiSettings.sidebar_hidden_worlds;
  }

  const hiddenItems = Array.isArray(uiSettings?.sidebar_hidden_menu_items)
    ? uiSettings.sidebar_hidden_menu_items
    : [];
  if (!hiddenItems.length) return [];

  const hiddenSet = new Set(hiddenItems);
  return HUB_WORLDS.filter(world => {
    // Sistema recebe apps dinâmicos que nunca entraram na preferência antiga.
    // Esconder o mundo apagaria um destino que a pessoa via.
    if (world.id === 'system') return false;
    const names = worldPreferenceNames(world);
    return names.length > 0 && names.every(name => hiddenSet.has(name));
  }).map(world => world.id);
};
